import { Box, Button, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { ManagementFilters, ManagementOptions, ManagementReportKey, Period, StudentStage } from '../../api/managementReports';
import { BRANCH_ONLY, DEFAULT_PERIOD, INTAKE_BASED, NO_PERIOD, PERIOD_LABELS, REPORT_FILTERS, STAGE_LABELS } from './filterParams';

const PRESETS: Period[] = ['1m', '2m', '3m', '6m', '1y', 'custom', 'all'];

/** The filters every Management Report shares (period, branch, university,
 * programme, intake, counsellor) plus the ones a report adds. */
export const ManagementFilterBar = ({
  report,
  filters,
  options,
  onChange,
}: {
  report: ManagementReportKey;
  filters: ManagementFilters;
  options: ManagementOptions | undefined;
  onChange: (next: ManagementFilters) => void;
}) => {
  const set = (patch: Partial<ManagementFilters>) => onChange({ ...filters, ...patch });
  const extra = REPORT_FILTERS[report];
  const universities = options?.universities.filter((u) => !filters.countryId || u.countryId === filters.countryId) ?? [];
  const courses =
    options?.courses.filter((c) => (filters.universityId ? c.universityId === filters.universityId : universities.some((u) => u.id === c.universityId))) ?? [];
  const intakeBased = INTAKE_BASED.includes(report);
  const showPeriod = !NO_PERIOD.includes(report);
  const branchOnly = BRANCH_ONLY.includes(report);
  const select = (
    label: string,
    value: number | string | undefined,
    items: Array<{ id: number | string; name: string; isActive?: boolean }>,
    allLabel: string,
    onSelect: (v: string) => void,
    width = 190,
  ) => (
    <TextField select size="small" label={label} value={value ?? ''} onChange={(e) => onSelect(e.target.value)} sx={{ width: { xs: '100%', sm: width } }}>
      <MenuItem value="">
        <em>{allLabel}</em>
      </MenuItem>
      {items.map((i) => (
        <MenuItem key={i.id} value={i.id}>
          {i.name}
          {i.isActive === false ? ' (inactive)' : ''}
        </MenuItem>
      ))}
    </TextField>
  );
  const num = (v: string) => (v === '' ? undefined : Number(v));
  const customInvalid = filters.period === 'custom' && !!filters.from && !!filters.to && filters.from > filters.to;

  return (
    <Box sx={{ mb: 2 }}>
      {showPeriod && (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
          {report === 'dropouts' ? 'Registration period' : intakeBased ? 'Intakes starting in' : 'Period'}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filters.period}
          onChange={(_e, value: Period | null) => value && set({ period: value, ...(value !== 'custom' ? { from: undefined, to: undefined } : {}) })}
          sx={{ flexWrap: 'wrap' }}
          aria-label="Reporting period"
        >
          {PRESETS.map((p) => (
            <ToggleButton key={p} value={p} sx={{ px: 1.25, py: 0.4, textTransform: 'none' }}>
              {PERIOD_LABELS[p]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {filters.period === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={filters.from ?? ''}
              onChange={(e) => set({ from: e.target.value || undefined })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={filters.to ?? ''}
              onChange={(e) => set({ to: e.target.value || undefined })}
              error={customInvalid}
              helperText={customInvalid ? '“To” is before “From”.' : undefined}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </>
        )}
      </Stack>
      )}
      {intakeBased && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.75, mb: 1.5 }}>
          This report selects students by the month their intake starts, whenever they registered. Rolling periods look back from today — use a custom range or
          All time for future intakes.
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        {options?.canChooseBranch &&
          select('Branch', filters.branchId, options.branches, 'All branches', (v) => set({ branchId: num(v) }))}
        {!branchOnly && (
          <>
            {select('Country', filters.countryId, options?.countries ?? [], 'All countries', (v) =>
              set({ countryId: num(v), universityId: undefined, courseId: undefined }),
            )}
            {select('University', filters.universityId, universities, 'All universities', (v) => set({ universityId: num(v), courseId: undefined }))}
            {select('Programme', filters.courseId, courses, 'All programmes', (v) => set({ courseId: num(v) }), 210)}
            {select('Intake', filters.intakeId, options?.intakes ?? [], 'All intakes', (v) => set({ intakeId: num(v) }))}
          </>
        )}
        {extra.includes('counselor') &&
          select('Counsellor', filters.counselorId, options?.counsellors ?? [], 'All counsellors', (v) => set({ counselorId: num(v) }))}
        {extra.includes('leadStatus') &&
          select('Lead status (now)', filters.leadStatusId, options?.leadStatuses ?? [], 'All statuses', (v) => set({ leadStatusId: num(v) }))}
        {extra.includes('stage') &&
          select(
            'Student status',
            filters.stage,
            (Object.keys(STAGE_LABELS) as StudentStage[]).map((s) => ({ id: s, name: STAGE_LABELS[s] })),
            'All students',
            (v) => set({ stage: (v || undefined) as StudentStage | undefined }),
          )}
        {extra.includes('dropoutPeriod') && (
          <>
            <TextField
              size="small"
              type="date"
              label="Dropped out from"
              value={filters.dropoutFrom ?? ''}
              onChange={(e) => set({ dropoutFrom: e.target.value || undefined })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              type="date"
              label="Dropped out to"
              value={filters.dropoutTo ?? ''}
              onChange={(e) => set({ dropoutTo: e.target.value || undefined })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </>
        )}
        <Button size="small" startIcon={<RestartAltIcon />} onClick={() => onChange({ period: DEFAULT_PERIOD })}>
          Reset
        </Button>
      </Stack>
    </Box>
  );
};
